import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  type AssignBrandUserInput,
  type ListBrandUsersInput,
  StaffBrandUsersService,
} from "@/services/staff-brand-users.service";
import { adsHttpClient } from "@/utils/ads.http";
import { useDebouncedValue } from "./use-debounced-value";

const EMAIL_LOOKUP_DELAY_MS = 300;
const USER_SEARCH_DELAY_MS = 300;
const emailSchema = z.string().email();

export const staffBrandUsersService = new StaffBrandUsersService(adsHttpClient);

export function useOnboardedBrands() {
  return useQuery({
    queryKey: ["staff-onboarded-brands"],
    queryFn: () => staffBrandUsersService.listOnboardedBrands(),
  });
}

export function useBrandUsers(input: ListBrandUsersInput = {}) {
  const query = input.query?.trim() ?? "";
  const debouncedQuery = useDebouncedValue(query, USER_SEARCH_DELAY_MS);
  const isDebouncing = query !== debouncedQuery;
  const request = {
    query: debouncedQuery,
    page: input.page ?? 1,
    limit: input.limit ?? 25,
  };

  const result = useQuery({
    queryKey: ["staff-brand-users", request],
    queryFn: () => staffBrandUsersService.listUsers(request),
  });

  return { ...result, isDebouncing };
}

export function useStaffBrandUserByEmail(email: string) {
  const normalizedEmail = email.trim().toLowerCase();
  const isValidEmail = emailSchema.safeParse(normalizedEmail).success;
  const debouncedEmail = useDebouncedValue(
    normalizedEmail,
    EMAIL_LOOKUP_DELAY_MS,
  );
  const isDebouncing = isValidEmail && debouncedEmail !== normalizedEmail;
  const query = useQuery({
    queryKey: ["staff-brand-user", debouncedEmail],
    queryFn: () => staffBrandUsersService.findByEmail(debouncedEmail),
    enabled: isValidEmail && !isDebouncing,
    retry: false,
  });

  return {
    ...query,
    isDebouncing,
    isValidEmail,
    normalizedEmail,
  };
}

export function useAssignBrandUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AssignBrandUserInput) =>
      staffBrandUsersService.assign(input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["staff-onboarded-brands"] }),
        queryClient.invalidateQueries({ queryKey: ["staff-brand-users"] }),
      ]);
    },
  });
}
